<template>
    <div class="flex flex-col mt-8">
        <PaginationNav v-if="paginated.length && hasTopNav" @flip="setPage" @limit-change="handleLimitChange"/>
        <div class="my-4">
            <component :is="element" v-if="paginated.length" :class="classes">
                <slot name="card" :items="paginated"></slot>
            </component>
            <p v-else class="py-4 px-5 sm:px-6">
                <slot name="none">There are currently no items.</slot>
            </p>
        </div>
        <PaginationNav v-if="paginated.length" @flip="setPage" @limit-change="handleLimitChange"/>
    </div>
</template>

<script>
import { computed } from 'vue';
import PaginationNav from './PaginationNav.vue';
import { mapState, mapActions } from 'pinia';
import { usePrefsStore } from '@/stores/preferences';

export default {
    data() {
        return {
            page: 1
        }
    },
    methods: {
        ...mapActions(usePrefsStore, ['setPref']),
        setPage(direction) {
            if (this.serverSide) {
                // For server-side pagination, emit event and let parent handle it
                let newPage;
                switch(direction) {
                    case 'next':
                        newPage = this.currentPage + 1;
                        break;
                    case 'prev':
                        newPage = this.currentPage - 1;
                        break;
                    default:
                        newPage = Number(direction);
                        break;
                }
                newPage = Math.max(1, Math.min(newPage, this.max));
                this.$emit('page-change', newPage);
            } else {
                // Client-side pagination
                let newPage;
                switch(direction) {
                    case 'next':
                        newPage = this.page + 1;
                        break;
                    case 'prev':
                        newPage = this.page - 1;
                        break;
                    default:
                        newPage = Number(direction);
                        break;
                }
                this.page = Math.max(1, Math.min(newPage, this.max));
            }
        },
        handleLimitChange(newLimit) {
            if (this.serverSide) {
                this.$emit('limit-change', newLimit);
            }
        }
    },
    computed: {
        ...mapState(usePrefsStore, ['limit']),
        currentPage() {
            if (this.serverSide && this.serverPagination) {
                return Math.floor(this.serverPagination.start / this.serverPagination.limit) + 1;
            }
            return this.page;
        },
        currentLimit() {
            if (this.serverSide && this.serverPagination) {
                return this.serverPagination.limit;
            }
            return this.limit;
        },
        totalCount() {
            if (this.serverSide && this.serverPagination) {
                return this.serverPagination.count;
            }
            return this.data.length;
        },
        max() {
            return Math.ceil(this.totalCount / this.currentLimit);
        },
        paginated() {
            if (this.serverSide) {
                // Server already paginated the data
                return this.data;
            }
            return this.data.slice(this.from, this.to);
        },
        from() {
            if (this.serverSide && this.serverPagination) {
                return this.serverPagination.start;
            }
            return (this.page * this.limit) - this.limit;
        },
        to() {
            if (this.serverSide && this.serverPagination) {
                return this.serverPagination.start + this.data.length;
            }
            let to = this.page * this.limit;
            return to <= this.data.length ? to : this.data.length;
        },
        classes() {
            let required = "grid gap-4".split(' ');
            let base = (this.$attrs.class ?? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6").split(' ');
            return [...new Set([...required, ...base])].join(' ');
        }
    },
    props: {
        data: {
            type: Array,
            required: true,
            default: () => []
        },
        element: {
            type: String,
            default: 'ul'
        },
        default: {
            type: Number,
            default: 24
        },
        serverSide: {
            type: Boolean,
            default: false
        },
        serverPagination: {
            type: Object,
            default: null
        },
        hasTopNav: {
            type: Boolean,
            default: false
        }
    },
    provide() {
        return {
            from: computed(() => this.from),
            to: computed(() => this.to),
            count: computed(() => this.totalCount),
            page: computed(() => this.currentPage)
        }
    },
    watch: {
        data: {
            handler() {
                // Only reset page for client-side pagination
                if (!this.serverSide) {
                    this.page = 1;
                }
            },
            deep: true
        }
    },
    components: {
        PaginationNav
    },
    created() {
        // Get valid limit values from PaginationNav limits prop
        const validLimits = [12, 24, 48, 96, 192];

        // Reset to default if current limit is not in valid limits
        if(!validLimits.includes(this.limit)) {
            this.setPref('limit', this.default);
        }
    }
}
</script>